import { useState, useEffect, useCallback, useRef } from 'react'
import { useUserId } from './useUserId'
import { clientLogger } from '../utils/clientLogger'
import {
  ShoppingList,
  ShoppingListItem,
  ShoppingListStatus,
  ShoppingListGenerationOptions
} from '../types/shoppingList'
import { Recipe } from '../types/recipe'
import { shoppingListService } from '../services/shoppingListService'

export function useShoppingList() {
  const userId = useUserId() // Custom UUID architecture - works for both guest and authenticated users
  const [shoppingLists, setShoppingLists] = useState<ShoppingList[]>([])
  const [activeList, setActiveList] = useState<ShoppingList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sharing, setSharing] = useState(false)

  // Stable ref for accessing latest state in callbacks
  const activeListRef = useRef<ShoppingList | null>(null)
  activeListRef.current = activeList

  // Load shopping lists on mount and auth changes
  useEffect(() => {
    loadShoppingLists()
  }, [userId])

  const loadShoppingLists = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const userIdOrAnonymous = userId || 'anonymous'
      clientLogger.debug({ userId: userIdOrAnonymous }, 'Loading shopping lists for user')

      const lists = shoppingListService.loadShoppingLists(userIdOrAnonymous)
      setShoppingLists(lists)

      // Set active list (most recent non-completed)
      const active = shoppingListService.getActiveShoppingList(userIdOrAnonymous)
      setActiveList(active)

      clientLogger.debug({ count: lists.length, activeName: active?.name || 'none' }, 'Loaded shopping lists')
    } catch (err) {
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error loading shopping lists')
      setError('Failed to load shopping lists')
    } finally {
      setLoading(false)
    }
  }, [userId])

  const generateShoppingList = useCallback(async (
    recipes: Recipe[],
    mealPlanId?: string,
    options?: ShoppingListGenerationOptions
  ): Promise<ShoppingList> => {
    setError(null)

    try {
      const userIdOrAnonymous = userId || 'anonymous'
      clientLogger.debug({ recipeCount: recipes.length }, 'Generating shopping list')

      const shoppingList = await shoppingListService.generateShoppingList(recipes, mealPlanId, options)

      // Save to storage
      shoppingListService.saveShoppingList(shoppingList, userIdOrAnonymous)

      // Update local state
      setShoppingLists(prev => [shoppingList, ...prev])
      setActiveList(shoppingList)

      clientLogger.debug({ name: shoppingList.name }, 'Generated and saved shopping list')
      return shoppingList
    } catch (err) {
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error generating shopping list')
      setError('Shopping list unavailable — please try again')
      throw err
    }
  }, [userId])

  const updateShoppingListItem = useCallback((
    itemId: string,
    updates: Partial<ShoppingListItem>
  ) => {
    const currentList = activeListRef.current
    if (!currentList) {
      clientLogger.warn({}, 'No active shopping list to update')
      return
    }

    try {
      const userIdOrAnonymous = userId || 'anonymous'
      clientLogger.debug({ itemId, updates }, 'Updating item')

      // Optimistic update
      const updatedList = {
        ...currentList,
        items: currentList.items.map(item =>
          item.id === itemId ? { ...item, ...updates } : item
        ),
        updatedAt: new Date().toISOString()
      }

      setActiveList(updatedList)
      setShoppingLists(prev =>
        prev.map(list => list.id === currentList.id ? updatedList : list)
      )

      // Persist to storage
      shoppingListService.updateShoppingListItem(currentList.id, itemId, updates, userIdOrAnonymous)

      clientLogger.debug({}, 'Updated shopping list item')
    } catch (err) {
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error updating shopping list item')
      setError('Failed to update item')
      // Revert optimistic update
      loadShoppingLists()
    }
  }, [userId, loadShoppingLists])

  const toggleItemChecked = useCallback((itemId: string, checked?: boolean) => {
    const currentList = activeListRef.current
    if (!currentList) return
    
    const item = currentList.items.find(item => item.id === itemId)
    if (!item) return
    
    const newChecked = checked !== undefined ? checked : !item.checked
    updateShoppingListItem(itemId, { checked: newChecked })
  }, [updateShoppingListItem])

  const updateItemQuantity = useCallback((itemId: string, newAmount: number) => {
    const currentList = activeListRef.current
    if (!currentList) return

    const userIdOrAnonymous = userId || 'anonymous'
    shoppingListService.updateItemQuantity(currentList.id, itemId, newAmount, userIdOrAnonymous)

    // Reload to reflect changes
    loadShoppingLists()
  }, [userId, loadShoppingLists])

  const updateShoppingListStatus = useCallback((
    status: ShoppingListStatus,
    listId?: string
  ) => {
    const targetListId = listId || activeListRef.current?.id
    if (!targetListId) {
      clientLogger.warn({}, 'No shopping list to update status')
      return
    }

    try {
      const userIdOrAnonymous = userId || 'anonymous'
      clientLogger.debug({ status }, 'Updating list status')

      // Update local state
      const updateList = (list: ShoppingList) => {
        if (list.id === targetListId) {
          const updated = {
            ...list,
            status,
            updatedAt: new Date().toISOString(),
            ...(status === 'completed' ? { completedAt: new Date().toISOString() } : {})
          }
          return updated
        }
        return list
      }

      setShoppingLists(prev => prev.map(updateList))
      if (activeListRef.current?.id === targetListId) {
        setActiveList(prev => prev ? updateList(prev) : prev)
      }

      // Persist to storage
      shoppingListService.updateShoppingListStatus(targetListId, status, userIdOrAnonymous)

      clientLogger.debug({}, 'Updated shopping list status')
    } catch (err) {
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error updating shopping list status')
      setError('Failed to update shopping list status')
    }
  }, [userId])

  const deleteShoppingList = useCallback((listId: string) => {
    try {
      const userIdOrAnonymous = userId || 'anonymous'
      clientLogger.debug({ listId }, 'Deleting shopping list')

      // Update local state
      setShoppingLists(prev => prev.filter(list => list.id !== listId))

      if (activeListRef.current?.id === listId) {
        // Find next active list or set to null
        const remainingLists = shoppingLists.filter(list => list.id !== listId)
        const nextActive = remainingLists.find(list =>
          list.status === 'active' || list.status === 'shopping'
        ) || null
        setActiveList(nextActive)
      }

      // Persist deletion
      shoppingListService.deleteShoppingList(listId, userIdOrAnonymous)

      clientLogger.debug({}, 'Deleted shopping list')
    } catch (err) {
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error deleting shopping list')
      setError('Failed to delete shopping list')
    }
  }, [userId, shoppingLists])

  const shareShoppingList = useCallback(async (listId?: string): Promise<void> => {
    const targetList = listId 
      ? shoppingLists.find(list => list.id === listId)
      : activeListRef.current

    if (!targetList) {
      clientLogger.warn({}, 'No shopping list to share')
      return
    }

    if (!navigator.share) {
      // Fallback for browsers without Web Share API
      clientLogger.warn({}, 'Web Share API not available, using fallback')
      const shareData = shoppingListService.generateShareData(targetList)
      
      try {
        await navigator.clipboard.writeText(shareData.text)
        // Could show a toast notification here
        clientLogger.debug({}, 'Shopping list copied to clipboard')
      } catch (err) {
        clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error copying to clipboard')
        setError('Failed to copy shopping list')
      }
      return
    }

    setSharing(true)
    setError(null)

    try {
      const shareData = shoppingListService.generateShareData(targetList)
      
      clientLogger.debug({ title: shareData.title }, 'Sharing shopping list')
      
      await navigator.share({
        title: shareData.title,
        text: shareData.text,
        ...(shareData.url && { url: shareData.url })
      })
      
      clientLogger.debug({}, 'Shopping list shared successfully')
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error sharing shopping list')
        setError('Failed to share shopping list')
      }
    } finally {
      setSharing(false)
    }
  }, [shoppingLists])

  const getShoppingListStats = useCallback((listId?: string) => {
    const targetList = listId 
      ? shoppingLists.find(list => list.id === listId)
      : activeListRef.current

    if (!targetList) {
      return {
        totalItems: 0,
        checkedItems: 0,
        completionPercentage: 0,
        itemsByCategory: {}
      }
    }

    return shoppingListService.getShoppingListStats(targetList)
  }, [shoppingLists])

  const groupItemsByCategory = useCallback((listId?: string) => {
    const targetList = listId 
      ? shoppingLists.find(list => list.id === listId)
      : activeListRef.current

    if (!targetList) {
      return {}
    }

    return shoppingListService.groupItemsByCategory(targetList.items)
  }, [shoppingLists])

  const setActiveShoppingList = useCallback((listId: string) => {
    const targetList = shoppingLists.find(list => list.id === listId)
    if (targetList) {
      setActiveList(targetList)
      clientLogger.debug({ name: targetList.name }, 'Set active shopping list')
    }
  }, [shoppingLists])

  const refreshShoppingLists = useCallback(() => {
    loadShoppingLists()
  }, [loadShoppingLists])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const hasActiveShoppingList = useCallback((): boolean => {
    return activeList !== null && (activeList.status === 'active' || activeList.status === 'shopping')
  }, [activeList])

  const canGenerateNewList = useCallback((): boolean => {
    // Allow generation if no active list or if active list is completed
    return !hasActiveShoppingList()
  }, [hasActiveShoppingList])

  return {
    // State
    shoppingLists,
    activeList,
    loading,
    error,
    sharing,
    
    // Actions
    generateShoppingList,
    updateShoppingListItem,
    toggleItemChecked,
    updateItemQuantity,
    updateShoppingListStatus,
    deleteShoppingList,
    shareShoppingList,
    setActiveShoppingList,
    refreshShoppingLists,
    clearError,
    
    // Computed/Helper functions
    getShoppingListStats,
    groupItemsByCategory,
    hasActiveShoppingList,
    canGenerateNewList,
  }
}